import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface WidgetInfoIconProps {
  description: string
}

function WidgetInfoIcon({ description }: WidgetInfoIconProps) {
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const anchorRef = useRef<HTMLButtonElement>(null)

  const updatePosition = useCallback(() => {
    const el = anchorRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const maxLeft = Math.max(8, window.innerWidth - 264)
    setCoords({
      top: rect.bottom + 4,
      left: Math.min(rect.left, maxLeft),
    })
  }, [])

  const show = useCallback(() => {
    updatePosition()
    setVisible(true)
  }, [updatePosition])

  const hide = useCallback(() => {
    setVisible(false)
  }, [])

  useLayoutEffect(() => {
    if (!visible) return
    updatePosition()
    const onScrollOrResize = () => updatePosition()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [visible, updatePosition])

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="inline-flex shrink-0 cursor-help rounded-sm text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1"
        aria-label={description}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {visible &&
        createPortal(
          <span
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
            className="pointer-events-none fixed z-[9999] w-max max-w-64 rounded-md bg-gray-900 px-2 py-1.5 text-[11px] font-normal normal-case tracking-normal text-white shadow-lg"
          >
            {description}
          </span>,
          document.body,
        )}
    </>
  )
}

export interface DashboardWidgetTitleProps {
  widgetId: string
  labelKey?: string
  className?: string
}

export default function DashboardWidgetTitle({
  widgetId,
  labelKey,
  className = '',
}: DashboardWidgetTitleProps) {
  const { t } = useTranslation(['dashboard'])
  const label = t(labelKey ?? `dashboard:widgets.${widgetId}`)
  const description = t(`dashboard:widgetDescriptions.${widgetId}`, { defaultValue: '' })

  return (
    <span className={`inline-flex min-w-0 items-center gap-1 ${className}`}>
      <span className="truncate">{label}</span>
      {description ? <WidgetInfoIcon description={description} /> : null}
    </span>
  )
}

export { WidgetInfoIcon }
